import { atom, selector, selectorFamily } from 'recoil';
import { apiService } from '../services/apiService';

// User authentication state
export const userAuthState = atom({
  key: 'userAuthState',
  default: {
    isAuthenticated: false,
    isValidating: false,
    validationError: null,
    telegramUser: null,
  },
});

// Current user data
export const currentUserState = atom({
  key: 'currentUserState',
  default: null,
});

// User duel data
export const userDuelState = atom({
  key: 'userDuelState',
  default: {
    currentDuel: null,
    duelHistory: [],
    isLoading: false,
    error: null,
  },
});

// WebSocket connection state
export const websocketState = atom({
  key: 'websocketState',
  default: {
    isConnected: false,
    reconnectAttempts: 0,
    lastMessage: null,
    subscribedChannels: [],
  },
});

// Selector to get user validation status
export const userValidationStatus = selector({
  key: 'userValidationStatus',
  get: ({ get }) => {
    const auth = get(userAuthState);
    return {
      isValidating: auth.isValidating,
      isAuthenticated: auth.isAuthenticated,
      error: auth.validationError,
    };
  },
});

// Selector to validate user with API
export const validateUserSelector = selectorFamily({
  key: 'validateUserSelector',
  get: (userData) => async () => {
    try {
      const result = await apiService.validateUser(userData);
      return result;
    } catch (error) {
      throw error;
    }
  },
});

// Selector to get user details
export const userDetailsSelector = selectorFamily({
  key: 'userDetailsSelector',
  get: (userId) => async () => {
    try {
      const result = await apiService.getUser(userId);
      return result;
    } catch (error) {
      throw error;
    }
  },
});

// Selector to get duel details
export const duelDetailsSelector = selectorFamily({
  key: 'duelDetailsSelector',
  get: (duelId) => async () => {
    try {
      const result = await apiService.getDuel(duelId);
      return result;
    } catch (error) {
      throw error;
    }
  },
});

// Computed selector for user display name
export const userDisplayNameSelector = selector({
  key: 'userDisplayNameSelector',
  get: ({ get }) => {
    const user = get(currentUserState);
    const telegramUser = get(userAuthState).telegramUser;

    if (user?.username) return user.username;
    if (telegramUser?.first_name && telegramUser?.last_name) {
      return `${telegramUser.first_name} ${telegramUser.last_name}`;
    }
    if (telegramUser?.first_name) return telegramUser.first_name;
    if (telegramUser?.username) return `@${telegramUser.username}`;

    return 'User';
  },
});

// Computed selector for user avatar
export const userAvatarSelector = selector({
  key: 'userAvatarSelector',
  get: ({ get }) => {
    const user = get(currentUserState);
    const telegramUser = get(userAuthState).telegramUser;

    return user?.avatar_url || telegramUser?.photo_url || null;
  },
});

// Computed selector for active duel status
export const activeDuelStatusSelector = selector({
  key: 'activeDuelStatusSelector',
  get: ({ get }) => {
    const duelState = get(userDuelState);
    const currentDuel = duelState.currentDuel;

    if (!currentDuel) return null;

    return {
      isActive: currentDuel.status === 'active',
      opponent: currentDuel.opponent,
      stake: currentDuel.stake,
      timeRemaining: currentDuel.endTime ? Math.max(0, currentDuel.endTime - Date.now()) : 0,
      userPosition: currentDuel.userPosition,
      opponentPosition: currentDuel.opponentPosition,
    };
  },
});
